import React, { useContext, useState } from 'react';
import { Modal, Typography } from 'antd';
import styled from 'styled-components';
import { Button, Input, SemanticColor } from '@bb/elements-web';
import { useDialogStore } from 'hooks/dialogStore/useDialogStore';
import { isValidEmail } from 'utils';
import { canLinkAccounts } from 'api/UserController/canLinkAccounts';
import { useMutation } from '@tanstack/react-query';
import { AuthContext } from 'contexts';
import { DIALOG_KEYS } from '../keys';
import { LinkAccountErrors } from './types';

const LINK_ACCOUNT_URL = `${process.env.AUTH_SERVER}/link-account`;

const LinkAccountModal = () => {
  const { auth } = useContext(AuthContext);
  const { email: currentEmail } = auth || {};
  const { removeDialog } = useDialogStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<LinkAccountErrors | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  const { mutateAsync: canSwitchAccountAsync } = useMutation({
    mutationFn: canLinkAccounts,
    onSuccess: (_, trimmedEmail) => {
      window.location.href = `${LINK_ACCOUNT_URL}?login_hint=${encodeURIComponent(currentEmail)}&target=${encodeURIComponent(trimmedEmail)}`;
    },
    onError: () => {
      // TODO: handle other error possibilities here
      setLoading(false);
      setError(LinkAccountErrors.GENERIC_ACCOUNT_LINK_ERROR);
    },
  });

  const onContinue = async () => {
    const trimmedEmail = email?.trim();

    if (!trimmedEmail) {
      setError(LinkAccountErrors.EMPTY);
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError(LinkAccountErrors.INVALID_EMAIL);
      return;
    }

    setError(null);
    setLoading(true);
    await canSwitchAccountAsync(trimmedEmail);
  };

  const onChange = (e: string) => {
    if (!e?.trim()) {
      setError(null);
    }

    setEmail(e);
  };

  return (
    <Modal
      open
      centered
      closable
      style={{ maxWidth: 400 }}
      footer={null}
      keyboard
      onCancel={() => removeDialog(DIALOG_KEYS.LinkAccountModal)}
    >
      <StyledFlex>
        <StyledHeader>
          Link account for instant switching between workspaces
        </StyledHeader>
        <Typography style={{ marginBottom: 4 }}>
          Enter email of the account you&apos;d like to add
        </Typography>
        <Input
          // TODO: update elements-web, onKey event not supported
          error={!!error}
          value={email}
          onChange={onChange}
          placeholder="Email"
          disabled={loading}
          dataCy="link-account-input"
        />
        {error && error !== LinkAccountErrors.EMPTY && (
          <Typography.Text type="danger">{error}</Typography.Text>
        )}
        <StyledButton
          loading={loading}
          onClick={onContinue}
          dataCy="continue-button"
        >
          <StyledButtonText>Continue to secure sign-in</StyledButtonText>
        </StyledButton>
        <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
          You&apos;ll be redirected to verify this account
        </Typography.Text>
      </StyledFlex>
    </Modal>
  );
};

export default LinkAccountModal;

const StyledButtonText = styled(Typography)`
  color: ${SemanticColor.white};
  margin: auto;
`;

const StyledFlex = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledHeader = styled(Typography)`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 18px;
`;

const StyledButton = styled(Button)`
  && {
    margin-bottom: 18px;
    margin-top: 12px;
    display: flex;
    align-items: center;
    line-height: 30px;
    font-size: 14px;
    text-align: center;
    border-radius: 7px;
    font-weight: 400;
    min-width: auto;
    justify-content: space-between;
  }
`;
